import { Box, Flex } from '@chakra-ui/layout'
import { capitalize } from 'lodash'
import React, {
  useCallback,
  useContext, useMemo,
  useRef,
  useEffect,
  useState,
} from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { isRestrictedUser } from '../../../utils/roles'
import {
  MLHeaderActionbar,
  MLHeaderToolbar
} from '../../components/MediaLibrary'
import { ContextMenu } from '../../components/MediaLibrary/ContextMenu'
import type { RightClickContextMenuRef } from '../../components/MediaLibrary/ContextMenu/RightClickContextMenu'
import RightClickContextMenu from '../../components/MediaLibrary/ContextMenu/RightClickContextMenu'
import PreviewPanel from '../../components/MediaLibrary/Details/PreviewPanel'
import GenerateImageUsingAIBannerCTA from '../../components/MediaLibrary/GenerateImage/BannerCTA'
import PollUnseenNotificationsCount from '../../components/MediaLibrary/Notifications/PollUnseenNotificationsCount'
import Panel from '../../components/MediaLibrary/Panel'
import PollOperations from '../../components/MediaLibrary/PollOperations'
import MLView from '../../components/MediaLibrary/View'
import { MLSortParams } from '../../constants'
import { MLContext, UserContext, useUserPreferences } from '../../providers'
import {
  MediaLibraryActions
} from '../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'
import { MainContentHeadingBox } from '../Base/MainContent'
import { useMediaLibraryModals } from '../../components/MediaLibrary/hooks/useMediaLibraryModals'

const MLMainContentHeadingBox = styled(MainContentHeadingBox)`
  min-height: initial;
  max-height: initial;
  padding-top: 12px;
  padding-bottom: 12px;
`
const MediaLibraryLayout = (): React.ReactElement => {
  const { t } = useTranslation()

  const { user } = useContext(UserContext)

  const { isPublicLink } = useContext(PublicLinksInfoContext)

  const [state, dispatch] = useContext(MLContext)

  // Add ref for context menu positioning
  const contextMenuRef = useRef<RightClickContextMenuRef | null>(null)
  
  // Add ref for heading box height calculation
  const headingBoxRef = useRef<HTMLDivElement>(null)

  const onClickForMain = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      e.stopPropagation();
      contextMenuRef.current?.closeMenu()
    },
    [],
  )

  // Add right-click handler for empty areas
  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      if ((e.target as HTMLElement).tagName === 'DIV') {
        e.preventDefault()
        e.stopPropagation()
        contextMenuRef.current?.handleContextMenu(e.nativeEvent)
      }
    },
    [],
  )

  const resetSearch = useCallback(() => {
    // if reset search is clicked with state variables set to default,
    // then useMLSearch hook won't fire request automatically
    if (
      state.q === "" &&
      state.searchMode === 'keyword' &&
      state.searchInputMode === 'builder' &&
      state.searchByTags === true &&
      state.fileType === "" &&
      state.searchQuery === '' &&
      state.filePage === 0 &&
      state.folderPage === 0 &&
      !state.isVersionIncludedInSearch
    ) {
      void state.refetch()
    }

    dispatch([
      {
        type: MediaLibraryActions.RESET_SEARCH,
      },
      {
        type: MediaLibraryActions.SET_Q,
        payload: '',
      },
      {
        type: MediaLibraryActions.SET_SEARCH_MODE,
        payload: 'keyword',
      },
      {
        type: MediaLibraryActions.SET_SEARCH_INPUT_MODE,
        payload: 'builder',
      },
      {
        type: MediaLibraryActions.SET_SEARCH_BY_TAGS,
        payload: true,
      },
      {
        type: MediaLibraryActions.SET_SEARCH_FILE_TYPE,
        payload: "",
      },
      {
        type: MediaLibraryActions.SET_IS_VERSION_INCLUDED_IN_SEARCH,
        payload: false,
      },
      {
        type: MediaLibraryActions.SET_SORT_PARAM,
        payload: MLSortParams.CREATED,
      },
      { type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS },
      {
        type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
        payload: false,
      },
      {
        type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
        payload: false,
      },
    ])
  }, [
    state.q,
    state.searchMode,
    state.searchInputMode,
    state.searchByTags,
    state.fileType,
    state.searchQuery,
    state.filePage,
    state.folderPage,
    state.folderPath,
    state.sort,
    state.refetch,
    state.isVersionIncludedInSearch,
  ])

  const title = useMemo(() => {
    if (state.searchQuery) {
      return t('search_results', 'Search Results')
    }

    if (state.folderPath === '/') {
      if (isRestrictedUser(user?.roles)) {
        return t('shared_with_me', 'Shared with me')
      }
      return t('media_library', 'Media Library')
    }
    return capitalize(state.folderPath.split('/').pop())
  }, [state.folderPath, state.searchQuery, user?.roles])
  const scrollRef = useRef<HTMLDivElement>(null)

  const { prefs } = useUserPreferences()

  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [actionBarHeight, setActionBarHeight] = useState<string>("60px");

  useEffect(() => {
    setScrollEl(scrollRef.current)
  }, [scrollRef.current])

  useEffect(() =>{

    const handleScroll = (event: Event) => {
      dispatch({type:MediaLibraryActions.CLOSE_CONTEXT_MENU})
    }
    if(scrollRef.current){
      scrollRef.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if(scrollRef.current){
        scrollRef.current.removeEventListener('scroll', handleScroll);
      }
    }
  }, [scrollRef.current])

  useEffect(() => {
    const updateHeadingHeight = () => {
      const headingHeight = headingBoxRef.current?.offsetHeight || 0
     setActionBarHeight(`${headingHeight}px`);
    }

    updateHeadingHeight()
    // Add ResizeObserver for responsive updates
    const resizeObserver = new ResizeObserver(updateHeadingHeight)
    if (headingBoxRef.current) resizeObserver.observe(headingBoxRef.current)
    return () => resizeObserver.disconnect()
  }, [])

  const controllers = useMediaLibraryModals()

  return (
    <>
      <Helmet title={title} />
      <Flex
        direction="column"
        alignItems="stretch"
        onContextMenu={handleContextMenu}
        onClick={onClickForMain}
      >
        <MLMainContentHeadingBox ref={headingBoxRef}>
          <MLHeaderActionbar resetSearch={resetSearch} />
        </MLMainContentHeadingBox>
        <MLHeaderToolbar
          resetSearch={resetSearch}
          controllers={controllers}
        />
        <ContextMenu controllers={controllers} />
        <Flex direction="row" overflow="hidden">
          <Box
            height={`calc(100vh - ${actionBarHeight} - 58px - 24px)`} //action bar + toolbar + pollOperations
            ref={scrollRef}
            flex={1}
            overflow={"auto"}
            className='media-library-layout'
            sx={{
              scrollbarGutter: 'stable',
              scrollbarWidth: 'thin',
            }}
          >
            <Box> {/*do not remove this Box wrapper, On removing this background scroll state is lost in file modal */}
            <RightClickContextMenu ref={contextMenuRef} />
            <MLView scrollEl={scrollEl} minHeight={`calc(100vh - ${actionBarHeight} - 58px - 24px)`} />
            </Box>
          </Box>
          {!isPublicLink && prefs.previewVisible && (
            <Box flexShrink={0} w="377px">
              <PreviewPanel isDetailPage={false} />
            </Box>
          )}
        </Flex>
      </Flex>

      <PollOperations />
      {!isPublicLink && <PollUnseenNotificationsCount />}
      <Panel />
    </>
  )
}

export default MediaLibraryLayout
